import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqItems } from '@/mocks/faq';

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const latestArticles = articles.slice(0, 4);

  const stats = [
    { value: '42%', label: '网民遭遇流氓视频侵扰', icon: 'ri-emotion-sad-line' },
    { value: '120亿+', label: '年度视频诈骗经济损失（元）', icon: 'ri-money-cny-circle-line' },
    { value: '15%', label: '受害者遭受直接经济损失', icon: 'ri-error-warning-line' },
    { value: '80%+', label: '通过学习可避免的风险', icon: 'ri-shield-check-line' },
  ];

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '网络安全守护',
    url: 'https://wangluoanquan.cn',
    description: '全面解析流氓视频的定义、常见传播方式及危害，教你如何识别恶意视频链接、保护个人隐私与设备安全',
    inLanguage: 'zh-CN',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '网络安全守护',
    url: 'https://wangluoanquan.cn',
    description: '致力于普及网络安全知识，帮助网民识别和防范流氓视频与网络诈骗',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'help@wangluoanquan.cn',
      contactType: 'customer support',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const categoryColors = ['primary', 'accent', 'secondary', 'primary'];
  const categoryIcons = ['ri-virus-line', 'ri-eye-line', 'ri-shield-check-line', 'ri-alert-line'];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="流氓视频是什么？识别与防范网络流氓视频内容全指南"
        description="本页详细解析「流氓视频」的定义、常见传播方式及危害，教你如何识别恶意视频链接、保护个人隐私与设备安全，提供实用防范技巧与举报渠道，守护你的网络安全。"
        keywords="流氓视频,恶意视频链接,网络安全防范,流氓软件视频,识别恶意内容,视频诈骗,网络流氓行为"
        canonicalUrl="https://wangluoanquan.cn"
      />
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[600px] md:h-[700px] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Dark%20cybersecurity%20concept%20with%20abstract%20digital%20shield%20and%20glowing%20red%20warning%20symbols%2C%20network%20security%20visualization%20with%20interconnected%20nodes%20and%20data%20streams%2C%20dramatic%20lighting%20with%20deep%20crimson%20and%20charcoal%20tones%2C%20futuristic%20digital%20art%20style%2C%20no%20text%2C%20abstract%20geometric%20patterns&width=1920&height=1080&seq=hero-bg-2026&orientation=landscape"
                alt="网络安全防护背景"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
            <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/20 border border-primary-400/30 mb-6">
                <i className="ri-shield-flash-line text-primary-400 text-sm"></i>
                <span className="text-sm text-primary-200">网络安全科普平台</span>
              </div>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                守护网络安全，<br className="hidden sm:block" />
                从识别<span className="text-primary-400">流氓视频</span>开始
              </h1>
              <p className="text-base md:text-lg text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">
                全面解析流氓视频的定义、传播方式与危害，提供实用的识别技巧、防范措施和举报渠道，守护你和你家人的网络安全。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/shibie-exing-shipin"
                  className="px-8 py-3.5 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-eye-line mr-2"></i>学习识别技巧
                </Link>
                <Link
                  to="/category/fangfan-cuoshi"
                  className="px-8 py-3.5 bg-white/10 text-white rounded-lg font-medium text-sm hover:bg-white/20 border border-white/20 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-shield-check-line mr-2"></i>查看防范措施
                </Link>
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  流氓视频危害数据
                </h2>
                <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                  这些触目惊心的数字提醒我们，网络安全防护刻不容缓
                </p>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                {stats.map((stat, index) => (
                  <div key={index} className="bg-background-50 border border-background-200/60 rounded-lg p-6 text-center">
                    <div className={`w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full ${
                      index === 0 ? 'bg-primary-100 text-primary-600' :
                      index === 1 ? 'bg-accent-100 text-accent-600' :
                      index === 2 ? 'bg-secondary-100 text-secondary-600' :
                      'bg-primary-100 text-primary-600'
                    }`}>
                      <i className={`${stat.icon} text-xl`}></i>
                    </div>
                    <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-1">
                      {stat.value}
                    </div>
                    <p className="text-xs md:text-sm text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Categories Section */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  内容分类导航
                </h2>
                <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                  四大核心板块，从认识到防范，从识别到维权，全方位守护你的网络安全
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {categories.map((cat, index) => {
                  const colorKey = categoryColors[index] as 'primary' | 'accent' | 'secondary';
                  const bgClass = `bg-${colorKey}-100/80`;
                  const iconClass = `text-${colorKey}-600`;
                  const badgeBg = `bg-${colorKey}-100`;
                  const badgeText = `text-${colorKey}-700`;

                  return (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="group bg-background-50 rounded-lg border border-background-200/60 p-6 hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
                    >
                      <div className={`w-12 h-12 flex items-center justify-center rounded-lg ${bgClass} mb-4`}>
                        <i className={`${cat.icon} text-xl ${iconClass}`}></i>
                      </div>
                      <h3 className="font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                        {cat.name}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed mb-4">{cat.description}</p>
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full ${badgeBg} ${badgeText}`}>
                        浏览内容 <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Latest Articles */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-10 gap-4">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                    最新文章
                  </h2>
                  <p className="text-sm text-foreground-500">深入浅出，帮你掌握网络安全知识</p>
                </div>
                <Link
                  to="/category/liuman-video-dingyi"
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-product-shop="">
                {latestArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  常见问题解答
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  关于流氓视频，你最关心的问题都在这里
                </p>
              </div>
              <div className="flex flex-col gap-3">
                {faqItems.map((item, index) => (
                  <div
                    key={index}
                    className="bg-background-50 rounded-lg border border-background-200/60 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                    >
                      <h4 className="font-heading font-medium text-foreground-800 pr-4 text-sm md:text-base">
                        {item.question}
                      </h4>
                      <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${
                        openFaq === index ? 'rotate-45' : ''
                      }`}>
                        <i className="ri-add-line text-foreground-400"></i>
                      </div>
                    </button>
                    {openFaq === index && (
                      <div className="px-5 pb-5">
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="w-full px-4 md:px-6 py-20 bg-background-50">
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center rounded-full bg-primary-100">
                <i className="ri-shield-star-line text-2xl text-primary-600"></i>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                网络安全，人人有责
              </h2>
              <p className="text-foreground-500 text-sm md:text-base mb-8 max-w-xl mx-auto leading-relaxed">
                每一次学习、每一次分享、每一次举报，都是在为清朗网络空间贡献力量。让我们携手共建安全的网络环境。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/jubao-weiquan"
                  className="px-8 py-3.5 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-alert-line mr-2"></i>了解举报渠道
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3.5 bg-background-100 text-foreground-700 rounded-lg font-medium text-sm hover:bg-background-200 border border-background-200/60 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-information-line mr-2"></i>关于我们
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}